/**
 * KAN-755: дерево категорий каталога с чекбоксами для скоупа скидки.
 * Выбранная категория действует вместе с подкатегориями: потомки выбранной
 * показаны отмеченными и неактивными. Ветки сворачиваются; ветки с выбранными
 * категориями раскрыты при открытии.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useCatalogCategories } from '@/hooks/useCatalog';
import { buildCategoryTree, type CategoryNode } from '@/lib/catalogTree';

interface CategoryCheckTreeProps {
  value: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
}

/** Ветки, в которых есть выбранная категория (раскрываются сразу). */
function branchesWithSelected(nodes: readonly CategoryNode[], selected: ReadonlySet<string>): Set<string> {
  const open = new Set<string>();
  const walk = (node: CategoryNode): boolean => {
    let found = false;
    node.children.forEach((child) => {
      if (walk(child)) found = true;
    });
    if (found) open.add(node.id);
    return found || selected.has(node.id);
  };
  nodes.forEach(walk);
  return open;
}

interface NodeProps {
  node: CategoryNode;
  selected: ReadonlySet<string>;
  inherited: boolean;
  expanded: ReadonlySet<string>;
  onExpand: (id: string) => void;
  onToggle: (id: string) => void;
  disabled: boolean;
}

function TreeNode({ node, selected, inherited, expanded, onExpand, onToggle, disabled }: NodeProps) {
  const checked = inherited || selected.has(node.id);
  const isOpen = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  return (
    <li>
      <div className="flex items-center gap-1.5 py-0.5" style={{ paddingLeft: `${node.depth * 16}px` }}>
        {hasChildren ? (
          <button
            type="button"
            aria-label={isOpen ? `Свернуть: ${node.name}` : `Развернуть: ${node.name}`}
            aria-expanded={isOpen}
            className="rounded p-0.5 text-gray-500 hover:bg-gray-100"
            onClick={() => onExpand(node.id)}
          >
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-[18px]" />
        )}
        <label className="flex min-w-0 items-center gap-2 text-sm text-gray-800">
          <Checkbox
            checked={checked}
            disabled={disabled || inherited}
            aria-label={node.name}
            data-testid={`category-check-${node.id}`}
            onCheckedChange={() => onToggle(node.id)}
          />
          <span className="truncate">{node.name}</span>
          <span className="text-xs text-gray-400">{node.subtreeCount}</span>
        </label>
      </div>
      {hasChildren && isOpen && (
        <ul>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              selected={selected}
              inherited={checked}
              expanded={expanded}
              onExpand={onExpand}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function CategoryCheckTree({ value, onToggle, disabled = false }: CategoryCheckTreeProps) {
  const { data, isLoading, isError } = useCatalogCategories();
  const tree = useMemo(() => buildCategoryTree(data?.categories ?? []), [data]);
  const selected = useMemo(() => new Set(value), [value]);
  // null: пользователь ветки ещё не трогал, раскрыты ветки с выбранными.
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const openIds = expanded ?? branchesWithSelected(tree, selected);

  const onExpand = (id: string): void => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  if (isLoading) return <p className="text-sm text-gray-500">Загрузка категорий...</p>;
  if (isError) return <p className="text-sm text-red-600">Не удалось загрузить категории</p>;
  if (tree.length === 0) return <p className="text-sm text-gray-500">Категорий нет</p>;

  return (
    <div className="space-y-1" data-testid="category-check-tree">
      <p className="text-xs text-gray-500">Категория выбирается вместе с подкатегориями</p>
      <ul className="max-h-64 overflow-y-auto rounded-md border bg-white p-2">
        {tree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            selected={selected}
            inherited={false}
            expanded={openIds}
            onExpand={onExpand}
            onToggle={onToggle}
            disabled={disabled}
          />
        ))}
      </ul>
    </div>
  );
}
